import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Camera, Heart, Award, Users, ChevronRight } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

const stats = [
  { icon: Camera, number: "8000+", label: "Projects Completed" },
  { icon: Heart, number: "3000+", label: "Happy Couples" },
  { icon: Award, number: "25+ Years", label: "Industry Experience" },
  { icon: Users, number: "3 States", label: "Punjab • Haryana • Rajasthan" },
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-24">
      {/* Hero Section */}
      <section data-testid="about-hero" className="section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
                Our Story
              </span>
              <h1 className="font-serif text-5xl md:text-7xl font-medium text-white mb-8 leading-tight">
                About
                <br />
                <span className="text-gradient-gold">Godara Studios</span>
              </h1>
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                Founded in 2000, Godara Studios began with a deep passion for preserving 
                emotions, traditions, and timeless moments through photography. What started as 
                a local photography venture has grown into a trusted and well-recognized name in 
                wedding and portrait photography across North India.
              </p>
              <p className="text-white/60 text-lg leading-relaxed">
                For over two decades, we’ve had the privilege of working with families and couples 
                who’ve trusted us to document their most important milestones. With 8,000+ 
                projects completed and 3,000+ weddings covered, our journey is built on 
                experience, consistency, and genuine relationships with our clients.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1701764167311-0087cd670a40?w=800&q=85"
                  alt="Photographer at work"
                  className="w-full h-full object-cover img-zoom"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-surface p-6 border border-white/10 hidden md:block">
                <p className="text-gold font-serif text-lg italic">"Every frame tells a story"</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="about-stats" className="py-16 bg-surface">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                data-testid={`stat-${index}`}
                className="text-center"
              >
                <stat.icon size={32} strokeWidth={1} className="text-gold mx-auto mb-4" />
                <p className="font-serif text-4xl md:text-5xl text-white mb-2">{stat.number}</p>
                <p className="text-white/50 text-sm uppercase tracking-widest">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section data-testid="about-philosophy" className="section bg-[#050505]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1606216794079-73f85bbd57d5?w=500&q=85"
                    alt="Wedding moment"
                    className="w-full h-full object-cover img-zoom"
                    loading="lazy"
                  />
                </div>
                <div className="aspect-square overflow-hidden mt-8">
                  <img
                    src="https://images.unsplash.com/photo-1519741497674-611481863552?w=500&q=85"
                    alt="Couple portrait"
                    className="w-full h-full object-cover img-zoom"
                    loading="lazy"
                  />
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
                Our Philosophy
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mb-8">
                Passion Meets
                <br />
                Precision
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                At Godara Studios, we believe photography is not just about images — it’s about 
                emotion, storytelling, and authenticity. Every wedding, every portrait, and every 
                celebration has a unique story, and our goal is to capture it as naturally and 
                beautifully as possible.
              </p>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                With decades of experience behind the lens, we blend artistic vision with 
                technical excellence, ensuring that your photographs don’t just show moments — 
                they let you relive them.
              </p>
              <ul className="space-y-4">
                {["Artistic Vision", "Technical Excellence", "Emotional Storytelling", "Client-Focused Approach"].map((item, index) => (
                  <li key={item} className="flex items-center gap-4 text-white/80">
                    <span className="w-8 h-[1px] bg-gold" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="about-why-us" className="section bg-surface">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm uppercase tracking-[0.3em] mb-4 block">
              Why Choose Us
            </span>
            <h2 className="font-serif text-4xl md:text-6xl font-medium text-white">
              The Godara Difference
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Proven Experience",
                description: "With 25+ years in the industry and thousands of successful projects, our work speaks for itself.",
              },
              {
                title: "Personalized Approach",
                description: "Every client and every event is unique. We tailor our style and workflow to match your vision and story.",
              },
              {
                title: "Regional Expertise",
                description: "Deeply rooted in Punjabi culture while serving Haryana and Rajasthan, we understand traditions, rituals, and moments that matter most.",
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                data-testid={`why-card-${index}`}
                className="bg-[#050505] border border-white/5 p-8 hover:border-gold/30 transition-all duration-500"
              >
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-6">
                  <span className="text-gold font-serif text-xl">0{index + 1}</span>
                </div>
                <h3 className="font-serif text-2xl text-white mb-4">{item.title}</h3>
                <p className="text-white/60 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="about-cta" className="section bg-[#050505]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-4xl md:text-6xl font-medium text-white mb-6">
              Let's Create Together
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-10">
              Ready to discuss your project? We'd love to hear about your vision and how we can help bring it to life.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-btn"
              className="inline-flex items-center gap-2 bg-gold text-black px-8 py-4 text-sm uppercase tracking-widest font-bold hover:bg-white transition-all duration-500"
            >
              Get in Touch
              <ChevronRight size={18} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      <WhatsAppButton />
    </main>
  );
}
